/**
 * Queries for the sale event hubs (web plan W0.15).
 *
 * The live offers use `PUBLISHED_PROMOTIONS_RICH` with the event's tag filter
 * (`saleEventPromotionsWhere` in @/seo/saleEvents); only the "Brands that ran
 * a sale last year" visibility check needs its own document.
 */
import { gql } from '@apollo/client';
import { print, type DocumentNode, type FieldNode, type OperationDefinitionNode } from 'graphql';

/**
 * Whether one brand is publicly visible: `brandById` answers it on the public
 * mount (backend B0.3: draft, blocked or out-of-country → NOT_FOUND, deleted →
 * GONE), with its canonical `urlPath` so the row links straight to the brand's
 * final URL (no hop through the id URL's 301 once slugs are live). The page
 * never sends this document as is; it is the validated template
 * `participantBrandsQuery` repeats once per brand, so the backend's
 * `npm run validate:clients` still checks the field and its selection.
 */
export const SALE_PARTICIPANT_BRAND = gql`
  query SaleParticipantBrand($id: ID!) {
    brandById(id: $id) {
      id
      urlPath
    }
  }
`;

const TEMPLATE = SALE_PARTICIPANT_BRAND.definitions[0] as OperationDefinitionNode;
const TEMPLATE_FIELD = TEMPLATE.selectionSet.selections[0] as FieldNode;

/** The alias (and variable name) of the n-th brand in `participantBrandsQuery`. */
export const participantAlias = (index: number): string => `b${index}`;

/**
 * One request for every brand on an event's list: the template field repeated
 * under the aliases `b0`, `b1`, …, each with its own `$bN: ID!` variable, e.g.
 *
 *   query SaleParticipantBrands($b0: ID!, $b1: ID!) {
 *     b0: brandById(id: $b0) { id urlPath }
 *     b1: brandById(id: $b1) { id urlPath }
 *   }
 *
 * The ids travel as variables, never in the query text. One brand that is not
 * visible fails only its own alias (read the answer with `errorPolicy: 'all'`).
 * A list of N brands costs one request instead of N, which matters behind the
 * per-IP rate limit that mobile carriers' shared addresses already strain.
 */
export function participantBrandsQuery(count: number): DocumentNode | null {
  if (!Number.isSafeInteger(count) || count < 1) return null;
  const field = TEMPLATE_FIELD.name.value;
  const argument = TEMPLATE_FIELD.arguments?.[0]?.name.value ?? 'id';
  const selection = print(TEMPLATE_FIELD.selectionSet!);
  const aliases = Array.from({ length: count }, (_, i) => participantAlias(i));
  return gql(
    `query SaleParticipantBrands(${aliases.map((a) => `$${a}: ID!`).join(', ')}) {
      ${aliases.map((a) => `${a}: ${field}(${argument}: $${a}) ${selection}`).join('\n')}
    }`,
  );
}
